// src/components/settings/bombbomb-tab.jsx
//
// Owner-level "Connect BombBomb" screen — mirrors the Follow Up Boss tab.
// One team BombBomb account, one API key, stored in integrations/bombBomb.
// The functions-api backend proxies the video list so the key never reaches
// the browser of regular agents. Admin-only tab. Never log the raw key.
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Loader2, CheckCircle2, KeyRound, Video } from "lucide-react";
import { useStateValue } from "../../context/StateProvider";
import {
  getBombBombIntegration,
  saveBombBombIntegration,
  fetchBombBombVideos,
} from "../../utils/bombbomb";

export default function BombBombTab() {
  const [{ user }] = useStateValue();
  const [integ, setInteg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    setInteg((await getBombBombIntegration()) || {});
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const connected = !!integ?.apiKey;

  const connect = async () => {
    const key = apiKey.trim();
    if (!key) {
      toast.error("Paste your BombBomb API key first.");
      return;
    }
    setBusy(true);
    try {
      await saveBombBombIntegration(key, user?.id);
      await load();
      setApiKey("");
      setTestResult(null);
      toast.success("BombBomb connected.");
    } catch (e) {
      console.error("Save BombBomb key failed:", e);
      toast.error("Could not save — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    const res = await fetchBombBombVideos();
    setTesting(false);
    if (res.ok) {
      setTestResult({ ok: true, count: res.videos.length });
      toast.success(`Connected — found ${res.videos.length} video${res.videos.length === 1 ? "" : "s"}.`);
    } else {
      setTestResult({ ok: false, error: res.error });
      toast.error(res.error || "Test failed");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 text-sm p-6">
        <Loader2 className="animate-spin" size={16} /> Loading…
      </div>
    );
  }

  return (
    <div className="max-w-xl space-y-5">
      <div>
        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
          <Video size={18} /> BombBomb
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Connect the team's BombBomb account once, and every agent can drop
          BombBomb videos straight into transaction emails. The videos keep
          BombBomb's animated preview and play tracking.
        </p>
      </div>

      {connected && (
        <div className="flex items-center gap-2 text-sm font-semibold text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
          <CheckCircle2 size={16} /> Connected
          {integ?.connectedAt
            ? ` since ${new Date(integ.connectedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`
            : ""}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
        <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500">
          {connected ? "Replace API Key" : "API Key"}
        </label>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <KeyRound size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="Paste your BombBomb API key…"
              className="w-full text-sm rounded border border-slate-300 pl-8 pr-2 py-2"
            />
          </div>
          <button
            onClick={connect}
            disabled={busy}
            className="text-sm font-semibold text-white bg-themeColor rounded px-4 py-2 disabled:opacity-50"
          >
            {busy ? "Saving…" : connected ? "Update" : "Connect"}
          </button>
        </div>
        <p className="text-[11px] text-slate-400">
          Find it in BombBomb under Profile → Integrations → API. The key is
          stored for the whole team; agents never see it.
        </p>
      </div>

      {connected && (
        <div className="flex items-center gap-3">
          <button
            onClick={testConnection}
            disabled={testing}
            className="text-sm font-semibold text-slate-700 border border-slate-300 rounded px-4 py-2 disabled:opacity-50"
          >
            {testing ? "Testing…" : "Test Connection"}
          </button>
          {testResult?.ok && (
            <span className="text-sm text-green-700">✓ {testResult.count} videos found</span>
          )}
          {testResult && !testResult.ok && (
            <span className="text-sm text-red-600">{testResult.error}</span>
          )}
        </div>
      )}
    </div>
  );
}
